import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Crown } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, refreshUser, isAuthenticated } = useAuth();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('');
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      setStatus('error');
      setMessage('No payment session found');
      return;
    }
    
    // If user has token, poll for status
    if (token) {
      pollPaymentStatus(sessionId, 0);
    } else {
      // No token - show success message and redirect to login
      setStatus('success');
      setMessage('Payment successful! Please log in to access your upgraded account.');
    }
  }, [searchParams, token]);

  const pollPaymentStatus = async (sessionId, attemptCount = 0) => {
    if (attemptCount >= maxAttempts) {
      setStatus('pending');
      setMessage('Payment verification is taking longer than expected. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        setMessage(data.message || 'Payment successful! Your account has been upgraded to Professional.');
        // Refresh user data to get updated subscription
        if (refreshUser) {
          await refreshUser();
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setMessage('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setAttempts(prev => prev + 1);
      setTimeout(() => pollPaymentStatus(sessionId, attemptCount + 1), 2000);

    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
      setMessage('Error verifying payment. Please contact support if you were charged.');
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'loading':
        return (
          <>
            <div className="p-4 rounded-full bg-blue-100 mb-6">
              <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 mb-4">
              Verifying Payment...
            </h1>
            <p className="text-slate-600">
              Please wait while we confirm your payment.
            </p>
          </>
        );

      case 'success':
        return (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 15 }}
              className="p-4 rounded-full bg-gradient-to-r from-purple-100 to-cyan-100 mb-6"
            >
              <Crown className="w-12 h-12 text-purple-600" />
            </motion.div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 mb-4">
              Welcome to Pro! 🎉
            </h1>
            <p className="text-slate-600 mb-8">{message}</p>
            <div className="space-y-3">
              {token ? (
                <Button
                  onClick={() => navigate('/app/posts')}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white"
                >
                  Go to Posts Management
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/login')}
                  className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white"
                >
                  Log In to Your Account
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </>
        );

      case 'pending':
        return (
          <>
            <div className="p-4 rounded-full bg-yellow-100 mb-6">
              <Loader2 className="w-12 h-12 text-yellow-600" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 mb-4">
              Processing Payment
            </h1>
            <p className="text-slate-600 mb-8">{message}</p>
            <Button onClick={() => navigate('/posts')} variant="outline">
              Continue to App
            </Button>
          </>
        );

      case 'error':
        return (
          <>
            <div className="p-4 rounded-full bg-red-100 mb-6">
              <XCircle className="w-12 h-12 text-red-600" />
            </div>
            <h1 className="text-3xl font-heading font-bold text-slate-900 mb-4">
              Payment Issue
            </h1>
            <p className="text-slate-600 mb-8">{message}</p>
            <div className="flex gap-4">
              <Button onClick={() => navigate('/pricing')} variant="outline">
                Try Again
              </Button>
              <Button onClick={() => navigate('/posts')}>
                Continue to App
              </Button>
            </div>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-cyan-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="max-w-md w-full p-8 text-center shadow-xl">
          {renderContent()}
        </Card>
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
